import React, { useCallback, useRef, useState } from 'react'
import { useModal, useSheetMode } from '../../hooks/useModal'

export interface BestaetigungOptionen {
  titel: string
  /** Objektname, wird im Text fett dargestellt */
  objekt?: string
  /** Text vor dem Objektnamen, z. B. "Drohne" -> Drohne „Name“ */
  text?: React.ReactNode
  /** Hinweis auf Folgen */
  hinweis?: React.ReactNode
  bestaetigenText?: string
  abbrechenText?: string
  /** rot (Standard) oder neutral */
  gefahr?: boolean
  /** Dritte Möglichkeit (z. B. direkter Weg zu einer anderen Seite); schließt den Dialog wie „Abbrechen“ (Ergebnis false) */
  weitereAktion?: { text: string; onClick: () => void }
}

interface Props extends BestaetigungOptionen {
  onAbbrechen: () => void
  onBestaetigen: () => void
}

/** Modaler Bestätigungsdialog; Fokus liegt auf „Abbrechen“, Esc bricht ab, Knöpfe mind. 44 px. */
export function BestaetigungsDialog({ titel, objekt, text, hinweis, bestaetigenText = 'Löschen', abbrechenText = 'Abbrechen', gefahr = true, weitereAktion, onAbbrechen, onBestaetigen }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const sheet = useSheetMode()
  useModal(true, ref, onAbbrechen, '[data-abbrechen]')

  return (
    <div className={`fixed inset-0 z-[3100] flex justify-center ${sheet ? 'items-end' : 'items-center p-4'}`}>
      <div className="absolute inset-0 bg-black/60" onClick={onAbbrechen} />
      <div ref={ref} data-ui-modal="" role="alertdialog" aria-modal="true" aria-label={titel} tabIndex={-1}
        className={`relative w-full max-w-md bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700 p-6 outline-none ${sheet ? 'ui-sheet-in rounded-t-2xl pb-[max(1.5rem,env(safe-area-inset-bottom))] max-h-[90dvh] overflow-y-auto' : 'rounded-xl'}`}>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-3">{titel}</h2>
        <p className="text-gray-700 dark:text-gray-300 break-words">
          {text}{text && objekt ? ' ' : ''}{objekt && <strong className="font-semibold text-gray-900 dark:text-dark-text">„{objekt}“</strong>}
        </p>
        {hinweis && <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{hinweis}</p>}
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button data-abbrechen type="button" onClick={onAbbrechen}
            className="min-h-12 md:min-h-[44px] px-5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400">
            {abbrechenText}
          </button>
          {weitereAktion && <button type="button" data-testid="bestaetigung-weitere-aktion" onClick={() => { weitereAktion.onClick(); onAbbrechen() }}
            className="min-h-12 md:min-h-[44px] px-5 text-sm font-medium rounded-lg border border-primary-500 text-primary-700 dark:text-primary-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500">{weitereAktion.text}</button>}
          <button type="button" onClick={onBestaetigen}
            className={`min-h-12 md:min-h-[44px] px-5 text-sm font-semibold rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${gefahr ? 'bg-[#C6291A] hover:bg-[#a82216] focus:ring-[#C6291A]' : 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500'}`}>
            {bestaetigenText}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Promise-basierter Ersatz für window.confirm: `if (!(await bestaetigen({...}))) return` und `{dialog}` einmal rendern. */
export function useBestaetigung() {
  const [offen, setOffen] = useState<{ opt: BestaetigungOptionen; res: (v: boolean) => void } | null>(null)
  const bestaetigen = useCallback((opt: BestaetigungOptionen) => new Promise<boolean>(res => setOffen({ opt, res })), [])
  const schliessen = useCallback((v: boolean) => { setOffen(o => { o?.res(v); return null }) }, [])
  const abbrechen = useCallback(() => schliessen(false), [schliessen])
  const dialog = offen ? <BestaetigungsDialog {...offen.opt} onAbbrechen={abbrechen} onBestaetigen={() => schliessen(true)} /> : null
  return { bestaetigen, dialog }
}
